// Shared derivation of pickable "workplaces" from a flat org tree, used by the
// onboarding wizard and the admin assign-role dialog so both render the same
// grouped-by-market structure.

export type OrgTreeNode = { id: string; name: string; parent_id: string | null; depth: number };

export type Workplace = {
  id: string;
  label: string; // leaf name, e.g. "Shopee"
  market: string; // top-level market the node sits under, e.g. "Vietnam" — the group header; "" for the root
  sublabel: string; // path between market and node, e.g. "Marketplace"; "" for direct children
  isMarket: boolean; // the market node itself ("I work across all of Vietnam")
  isCompany: boolean; // the org root — entire company, every market
  ancestorIds: string[]; // ids of all ancestors, nearest first — access cascades down from any of them
};

// Depth-first pre-order (org_tree comes sorted by depth, position, so siblings
// keep their position order) — every node is immediately followed by its
// subtree, keeping children next to their parent.
export function deriveWorkplaces(orgTree: OrgTreeNode[]): Workplace[] {
  const byId = new Map(orgTree.map((n) => [n.id, n]));
  const names = (n: OrgTreeNode): string[] => {
    const segs: string[] = [];
    let cur: OrgTreeNode | undefined = n;
    while (cur) {
      segs.unshift(cur.name);
      cur = cur.parent_id ? byId.get(cur.parent_id) : undefined;
    }
    return segs;
  };
  const childrenOf = new Map<string | null, OrgTreeNode[]>();
  for (const n of orgTree) {
    const list = childrenOf.get(n.parent_id) ?? [];
    list.push(n);
    childrenOf.set(n.parent_id, list);
  }
  const ordered: OrgTreeNode[] = [];
  const visit = (n: OrgTreeNode) => {
    ordered.push(n);
    (childrenOf.get(n.id) ?? []).forEach(visit);
  };
  (childrenOf.get(null) ?? []).forEach(visit);
  const ancestorIds = (n: OrgTreeNode): string[] => {
    const ids: string[] = [];
    let cur = n.parent_id ? byId.get(n.parent_id) : undefined;
    while (cur) {
      ids.push(cur.id);
      cur = cur.parent_id ? byId.get(cur.parent_id) : undefined;
    }
    return ids;
  };
  return ordered.map((n) => {
    const path = names(n); // [root, market, …, leaf]
    return {
      id: n.id,
      label: n.name,
      market: path[1] ?? "",
      sublabel: path.slice(2, -1).join(" › "),
      isMarket: path.length === 2,
      isCompany: path.length === 1, // the root — entire company
      ancestorIds: ancestorIds(n),
    };
  });
}
